import { PageTemplate } from "@/views/PageTemplate";
import { Button } from "@/components/ui/button";
import { ArrowRight, ChevronRight } from "lucide-react";
import { ChartCard } from "@/components/ui/chart-card";
import { StatsCardContainer, StatsCardItem } from "@/components/ui/stats-card";
import dashboardIcon from "@/assets/icons/dashboard.svg?url";
import iUSDIcon from "@/assets/tokens/icon_i-onlight.svg?url";
import "./Dashboard.scss";
import { DashboardIcon } from "@/components/icons";
import { DetailsButton } from "@/components/Buttons/Details";

export default function Dashboard() {
  // Mock data for charts with defined types
  const tvlChartData: number[] = [10, 12, 8, 14, 16, 10, 15];
  const revenueChartData: number[] = [8, 10, 7, 12, 14, 9, 13];

  return (
    <PageTemplate title='Dashboard' subtitle='Current protocol metrics.' icon={<DashboardIcon active={true} className='h-[16px]' />}>
      <div className='grid grid-cols-1 md:grid-cols-2 gap-6 mb-[80px]'>
        {/* TVL Card */}
        <ChartCard title='Current Total Value Locked (TVL)' value='$23,459,654' data={tvlChartData} />

        {/* Revenue Card */}
        <ChartCard title='Total Revenue for the Interesting Protocol' value='$3,459,654' data={revenueChartData} />
      </div>

      {/* My Positions Section */}
      <div className='mb-16'>
        <div className='flex items-center mb-[40px]'>
          <h2 className='text-xl font-medium mr-[16px]'>My Positions</h2>
          <p className='text-sm text-gray-500'>Track your earnings, rewards, and activity in real time.</p>
        </div>

        <div className='mb-[80px]'>
          <StatsCardContainer>
            <StatsCardItem title='PIQUE Tokens' value='459,654' />
            <StatsCardItem title='Lifetime APY' value='154.8%' />
            <StatsCardItem title='Interesting... APY' value='16.4%' />
            <StatsCardItem title='PIQUE APY' value='2.3%' isLast={true} />
          </StatsCardContainer>
        </div>

        {/* Bonds Table */}
        <div className='mb-[35px]'>
          <table className='w-full border-collapse'>
            <thead>
              <tr className='text-left text-xs text-gray-500'>
                <th className='py-2 font-normal'>Bond</th>
                <th className='py-2 font-normal'>Initial Investment</th>
                <th className='py-2 font-normal'>Current Value</th>
                <th className='py-2 font-normal'>Time Left</th>
                <th className='py-2 font-normal text-center'>APR</th>
              </tr>
            </thead>
            <tbody>
              <tr className='border-t border-gray-100'>
                <td className='py-4 px-4'>
                  <div className='flex items-center'>
                    <img src={iUSDIcon} className='w-[18px] mr-[8px]' alt='iUSD' />
                    <span>iUSD Bond</span>
                  </div>
                </td>
                <td className='py-4 text-left'>$3,650</td>
                <td className='py-4'>
                  <div className='flex items-center'>
                    <span className='text-left'>$4,596</span>
                    <span className='text-(--green) text-xs ml-1'>+946</span>
                  </div>
                </td>
                <td className='py-4'>
                  <div className='text-left'>
                    <span className='text-xs text-left'>23 days</span>
                    <div className='w-full bg-gray-200 rounded-full h-1.5 mt-1'>
                      <div className='bg-(--green) h-1.5 rounded-full' style={{ width: "30%" }}></div>
                    </div>
                  </div>
                </td>
                <td className='py-4 px-4'>12.45%</td>
              </tr>
              <tr className='border-t border-b border-gray-100'>
                <td className='py-4 px-4'>
                  <div className='flex items-center'>
                    <img src={iUSDIcon} className='w-[18px] mr-[8px]' alt='iUSD' />
                    <span>iUSD Bond</span>
                  </div>
                </td>
                <td className='py-4 text-left'>$3,650</td>
                <td className='py-4'>
                  <div className='flex items-center'>
                    <span>$4,596</span>
                    <span className='text-(--green) text-xs ml-1'>+946</span>
                  </div>
                </td>
                <td className='py-4'>
                  <div className='text-left'>
                    <span className='text-xs'>23 days</span>
                    <div className='w-full bg-gray-200 rounded-full h-1.5 mt-1'>
                      <div className='bg-(--green) h-1.5 rounded-full' style={{ width: "30%" }}></div>
                    </div>
                  </div>
                </td>
                <td className='py-4 px-4'>12.45%</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className='flex justify-between items-center'>
          {/* Current Best Performer */}
          <div className='bg-white border rounded-md p-4 mb-4 w-fit'>
            <div className='flex items-center justify-between'>
              <div className='flex items-start mr-[16px]'>
                <div className='flex flex-col'>
                  <p className='text-sm font-small text-left'>Current Best Performer</p>
                  <div className='flex items-center'>
                    <img src={iUSDIcon} className='w-[18px] mr-[8px]' alt='iUSD' />
                    <span className=''>iUSD Bond</span>
                    <div className='flex items-center ml-4'>
                      <span className='mr-[4px]'>🚀</span> <span className=''>12.45% APR</span>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <DetailsButton>Details</DetailsButton>
              </div>
            </div>
          </div>

          {/* Explore Bonds Button */}
          <div className=''>
            <Button className='bg-blue-500 hover:bg-blue-600 text-white'>
              Explore Bonds <ChevronRight className='ml-2 w-4 h-4' />
            </Button>
          </div>
        </div>
      </div>

      {/* Earnings Overview */}
      <div className='mb-10'>
        <div className='flex items-center mb-4'>
          <h2 className='text-xl font-medium mr-[16px]'>Earnings Overview</h2>
          <p className='text-sm text-gray-500'>Track your earnings, rewards, and activity in real time.</p>
        </div>

        <div className='mb-[80px]'>
          <StatsCardContainer>
            <StatsCardItem title='PIQUE Tokens' value='459,654' />
            <StatsCardItem title='Lifetime APY' value='154.8%' />
            <StatsCardItem title='Interesting... APY' value='16.4%' />
            <StatsCardItem title='PIQUE APY' value='2.3%' isLast={true} />
          </StatsCardContainer>
        </div>

        {/* Bottom Bonds */}
        <table className='w-full border-collapse'>
          <thead>
            <tr className='text-left text-xs text-gray-500'>
              <th className='py-2 px-4 font-normal'>Bond</th>
              <th className='py-2 px-4 font-normal'>Initial Investment</th>
              <th className='py-2 px-4 font-normal'>Current Value</th>
              <th className='py-2 px-4 font-normal'>Time Left</th>
              <th className='py-2 px-4 font-normal text-center'>APR</th>
            </tr>
          </thead>
          <tbody>
            <tr className='border-t border-(--background)'>
              <td className='py-4 px-4'>
                <div className='flex items-center'>
                  <img src={iUSDIcon} className='w-[18px] mr-[8px]' alt='iUSD' />
                  <span>iUSD Bond</span>
                </div>
              </td>
              <td className='py-4 px-4'>$3,650</td>
              <td className='py-4 px-4'>
                <div className='flex items-center'>
                  <span>$4,596</span>
                  <span className='text-(--green) text-xs ml-1'>+946</span>
                </div>
              </td>
              <td className='py-4 px-4'>
                <div className='text-left'>
                  <span className='text-xs'>23 days</span>
                  <div className='w-full bg-gray-200 rounded-full h-1.5 mt-1'>
                    <div className='bg-(--green) h-1.5 rounded-full' style={{ width: "30%" }}></div>
                  </div>
                </div>
              </td>
              <td className='py-4 px-4'>12.45%</td>
            </tr>
            <tr className='border-t border-(--background)'>
              <td className='py-4 px-4'>
                <div className='flex items-center'>
                  <img src={iUSDIcon} className='w-[18px] mr-[8px]' alt='iUSD' />
                  <span>iUSD Bond</span>
                </div>
              </td>
              <td className='py-4 px-4'>$3,650</td>
              <td className='py-4 px-4'>
                <div className='flex items-center'>
                  <span>$4,596</span>
                  <span className='text-(--green) text-xs ml-1'>+946</span>
                </div>
              </td>
              <td className='py-4 px-4'>
                <div className='text-left'>
                  <span className='text-xs'>23 days</span>
                  <div className='w-full bg-gray-200 rounded-full h-1.5 mt-1'>
                    <div className='bg-(--green) h-1.5 rounded-full' style={{ width: "30%" }}></div>
                  </div>
                </div>
              </td>
              <td className='py-4 px-4'>12.45%</td>
            </tr>
          </tbody>
        </table>
      </div>
    </PageTemplate>
  );
}
